// Form to recover password
import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../../config/firebase/auth';
import Logo from '../../../assets/fonts/Solo-logo.png';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';

interface RecoverPasswordFormProps {
  onBack: () => void;
}

const RecoverPasswordForm: React.FC<RecoverPasswordFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Se ha enviado un correo para restablecer tu contraseña.');
    } catch (err: any) {
      setError('No se pudo enviar el correo de recuperación.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-yap-bg text-white selection:bg-yap-primary selection:text-black">
      
      {/* Background Ambience */}
      <div className="absolute top-[-20%] left-[20%] w-[600px] h-[600px] bg-yap-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Main Card */}
      <div className="w-full max-w-[480px] bg-zinc-900/40 backdrop-blur-2xl rounded-[32px] border border-white/5 shadow-2xl relative z-10 overflow-hidden">
        
        <div className="p-8 sm:p-10">
          {/* Header */}
          <div className="flex flex-col items-center mb-10">
            <img src={Logo} alt="YAP Logo" className="w-20 h-20 mb-6 object-contain" />
            <h1 className="text-3xl font-bold text-white tracking-tight">
              {message ? 'Revisa tu correo' : 'Recuperar contraseña'}
            </h1>
            <p className="text-zinc-400 text-sm mt-3 text-center leading-relaxed">
              {message ? (
                <>
                  Si el correo <span className="text-white font-medium bg-zinc-800/50 px-2 py-0.5 rounded-md text-xs">{email}</span> está registrado, recibirás un correo para restablecer tu contraseña.
                </>
              ) : (
                "Ingresa tu correo para recibir un link de recuperación."
              )}
            </p>
          </div>

          {/* Form Content */}
          <div className="relative min-h-[160px]">
            {!message ? (
              <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <div className={`group relative transition-all duration-300 ${error ? 'animate-shake' : ''}`}>
                    <div className="relative">
                      <Mail className="absolute left-4 top-4 h-5 w-5 text-zinc-500 group-focus-within:text-yap-primary transition-colors duration-300" />
                      <input
                        id="recover-email"
                        type="email"
                        autoFocus
                        autoComplete="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (error) setError(null);
                        }}
                        className={`w-full pl-12 pr-4 py-4 bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 focus:border-yap-primary rounded-2xl outline-none transition-all duration-300 font-medium text-white placeholder:text-zinc-400 focus:ring-1 focus:ring-yap-primary/50 ${
                          error ? '!border-red-500/50 focus:!border-red-500' : ''
                        }`}
                        placeholder="usuario@mail.com"
                      />
                    </div>
                  </div>
                  {error && <p className="text-red-400 text-xs pl-2 font-medium">{error}</p>}
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={!email || loading}
                    className="w-full bg-yap-primary hover:bg-yap-hover text-black font-bold py-4 rounded-2xl shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)] hover:shadow-[0_0_25px_-5px_rgba(0,255,132,0.5)] transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2 text-[15px]"
                  >
                    {loading ? <Loader2 className="animate-spin w-5 h-5" /> : "Enviar link de recuperación"}
                  </button>

                  <button
                    type="button"
                    onClick={onBack}
                    className="w-full text-zinc-500 hover:text-white flex items-center justify-center gap-1.5 transition-colors font-medium text-sm py-2"
                  >
                    <ArrowLeft size={14} /> Volver al inicio de sesión
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col items-center justify-center animate-fade-in space-y-6">
                <div className="w-20 h-20 bg-yap-primary/10 rounded-full flex items-center justify-center ring-1 ring-yap-primary/30">
                  <CheckCircle className="w-10 h-10 text-yap-primary" />
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-yap-primary hover:bg-yap-hover text-black font-bold py-4 rounded-2xl shadow-[0_0_20px_-5px_rgba(0,255,132,0.3)] hover:shadow-[0_0_25px_-5px_rgba(0,255,132,0.5)] transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2 text-[15px]"
                >
                  {loading ? <Loader2 className="animate-spin w-5 h-5" /> : "Reenviar link"}
                </button>

                <button
                  type="button"
                  onClick={onBack}
                  className="w-full text-zinc-500 hover:text-white flex items-center justify-center gap-1.5 transition-colors font-medium text-sm py-2"
                >
                  <ArrowLeft size={14} /> Volver al inicio de sesión
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-zinc-900/30 p-5 border-t border-white/5 text-center backdrop-blur-md">
          <p className="text-xs text-zinc-500 font-medium">
            Protegido por reCAPTCHA · <a href="#" className="hover:text-yap-primary transition-colors">Privacidad</a> · <a href="#" className="hover:text-yap-primary transition-colors">Términos</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RecoverPasswordForm;
